import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { User, Shield, Briefcase, GraduationCap, MapPin, Plus, X, Save, Loader2, Pencil, Trash2, Building2, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MILITARY_BRANCHES = ['Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force'];
const RANKS = ['E-1 to E-4', 'E-5 to E-6', 'E-7 to E-9', 'O-1 to O-3', 'O-4 to O-6', 'O-7+', 'W-1 to W-5'];
const INDUSTRIES = [
  'Technology', 'Healthcare', 'Finance', 'Government', 'Defense/Aerospace',
  'Manufacturing', 'Consulting', 'Education', 'Logistics', 'Energy'
];

const DEGREE_TYPES = [
  'High School Diploma', 'GED', 'Associate Degree', 'Bachelor\'s Degree', 
  'Master\'s Degree', 'Doctorate', 'Professional Certification', 'Trade Certificate'
];

const ProfileBuilder = () => {
  const { token, user } = useAuth();
  const [profile, setProfile] = useState({
    military_branch: '',
    rank: '',
    mos: '',
    years_of_service: '',
    discharge_status: '',
    skills: [],
    certifications: [],
    education: [],
    work_experience: [],
    career_goals: '',
    preferred_industries: [],
    preferred_locations: [],
    remote_preference: '',
    salary_expectation: '',
    summary: '',
    contact_info: { name: '', email: '', phone: '', location: '' }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [newCert, setNewCert] = useState('');
  const [newLocation, setNewLocation] = useState('');
  
  // Work Experience Modal State
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [editingWorkIndex, setEditingWorkIndex] = useState(null);
  const [workForm, setWorkForm] = useState({
    title: '',
    company: '',
    location: '',
    start_date: '',
    end_date: '',
    current: false,
    description: ''
  });

  // Education Modal State
  const [showEduModal, setShowEduModal] = useState(false);
  const [editingEduIndex, setEditingEduIndex] = useState(null);
  const [eduForm, setEduForm] = useState({
    degree: '',
    field: '',
    school: '',
    location: '',
    year: '',
    gpa: '',
    honors: ''
  });

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(prev => ({
        ...prev,
        ...response.data,
        education: response.data.education || [],
        work_experience: response.data.work_experience || [],
        contact_info: {
          name: response.data.contact_info?.name || user?.name || '',
          email: response.data.contact_info?.email || user?.email || '',
          phone: response.data.contact_info?.phone || '',
          location: response.data.contact_info?.location || ''
        }
      }));
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      setProfile(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setProfile(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addCertification = () => {
    if (newCert.trim() && !profile.certifications.includes(newCert.trim())) {
      setProfile(prev => ({ ...prev, certifications: [...prev.certifications, newCert.trim()] }));
      setNewCert('');
    }
  };

  const removeCertification = (cert) => {
    setProfile(prev => ({ ...prev, certifications: prev.certifications.filter(c => c !== cert) }));
  };

  const addLocation = () => {
    if (newLocation.trim() && !profile.preferred_locations.includes(newLocation.trim())) {
      setProfile(prev => ({ ...prev, preferred_locations: [...prev.preferred_locations, newLocation.trim()] }));
      setNewLocation('');
    }
  };

  const toggleIndustry = (industry) => {
    setProfile(prev => ({
      ...prev,
      preferred_industries: prev.preferred_industries.includes(industry)
        ? prev.preferred_industries.filter(i => i !== industry)
        : [...prev.preferred_industries, industry]
    }));
  };

  // Work Experience Functions
  const openAddWorkModal = () => {
    setWorkForm({
      title: '',
      company: '',
      location: '',
      start_date: '',
      end_date: '',
      current: false,
      description: ''
    });
    setEditingWorkIndex(null);
    setShowWorkModal(true);
  };

  const openEditWorkModal = (index) => {
    const work = profile.work_experience[index];
    setWorkForm({
      title: work.title || '',
      company: work.company || '',
      location: work.location || '',
      start_date: work.start_date || '',
      end_date: work.end_date || '',
      current: work.current || false,
      description: work.description || ''
    });
    setEditingWorkIndex(index);
    setShowWorkModal(true);
  };

  const saveWorkExperience = () => {
    if (!workForm.title || !workForm.company) {
      toast.error('Please enter job title and company');
      return;
    }

    const newWork = { ...workForm };
    if (newWork.current) {
      newWork.end_date = 'Present';
    }

    if (editingWorkIndex !== null) {
      const updated = [...profile.work_experience];
      updated[editingWorkIndex] = newWork;
      setProfile(prev => ({ ...prev, work_experience: updated }));
    } else {
      setProfile(prev => ({ ...prev, work_experience: [...prev.work_experience, newWork] }));
    }
    
    setShowWorkModal(false);
    toast.success(editingWorkIndex !== null ? 'Experience updated' : 'Experience added');
  };

  const deleteWorkExperience = (index) => {
    setProfile(prev => ({
      ...prev,
      work_experience: prev.work_experience.filter((_, i) => i !== index)
    }));
    toast.success('Experience removed');
  };

  // Education Functions
  const openAddEduModal = () => {
    setEduForm({
      degree: '',
      field: '',
      school: '',
      location: '',
      year: '',
      gpa: '',
      honors: ''
    });
    setEditingEduIndex(null);
    setShowEduModal(true);
  };

  const openEditEduModal = (index) => {
    const edu = profile.education[index];
    setEduForm({
      degree: edu.degree || '',
      field: edu.field || '',
      school: edu.school || '',
      location: edu.location || '',
      year: edu.year || '',
      gpa: edu.gpa || '',
      honors: edu.honors || ''
    });
    setEditingEduIndex(index);
    setShowEduModal(true);
  };

  const saveEducation = () => {
    if (!eduForm.degree || !eduForm.school) {
      toast.error('Please enter degree type and school');
      return;
    }

    if (editingEduIndex !== null) {
      const updated = [...profile.education];
      updated[editingEduIndex] = eduForm;
      setProfile(prev => ({ ...prev, education: updated }));
    } else {
      setProfile(prev => ({ ...prev, education: [...prev.education, eduForm] }));
    }
    
    setShowEduModal(false);
    toast.success(editingEduIndex !== null ? 'Education updated' : 'Education added');
  };

  const deleteEducation = (index) => {
    setProfile(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
    toast.success('Education removed');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-5xl" data-testid="profile-builder-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">Build Your Profile</h1>
          <p className="text-navy-600 dark:text-navy-300">Complete your profile to get personalized job matches and document templates</p>
        </div>

        <div className="space-y-6">
          {/* Contact Information */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                <User className="w-5 h-5 text-amber-600" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Full Name</Label>
                <Input
                  value={profile.contact_info.name}
                  onChange={(e) => setProfile(prev => ({ 
                    ...prev, 
                    contact_info: { ...prev.contact_info, name: e.target.value }
                  }))}
                  placeholder="John Smith"
                  className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Email</Label>
                <Input
                  value={profile.contact_info.email}
                  onChange={(e) => setProfile(prev => ({ 
                    ...prev, 
                    contact_info: { ...prev.contact_info, email: e.target.value }
                  }))}
                  placeholder="john@example.com"
                  className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                />
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Phone</Label>
                <Input
                  value={profile.contact_info.phone}
                  onChange={(e) => setProfile(prev => ({ 
                    ...prev, 
                    contact_info: { ...prev.contact_info, phone: e.target.value }
                  }))}
                  placeholder="(555) 123-4567"
                  className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                />
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Location</Label>
                <Input
                  value={profile.contact_info.location}
                  onChange={(e) => setProfile(prev => ({ 
                    ...prev, 
                    contact_info: { ...prev.contact_info, location: e.target.value }
                  }))}
                  placeholder="San Diego, CA"
                  className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                />
              </div>
            </CardContent>
          </Card>

          {/* Military Background */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                <Shield className="w-5 h-5 text-amber-600" />
                Military Background
              </CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Branch</Label>
                <Select 
                  value={profile.military_branch} 
                  onValueChange={(v) => setProfile(prev => ({ ...prev, military_branch: v }))}
                >
                  <SelectTrigger className="mt-1 dark:bg-navy-700 dark:border-navy-600" data-testid="profile-branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-navy-800 dark:border-navy-600">
                    {MILITARY_BRANCHES.map(branch => (
                      <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Rank Range</Label>
                <Select 
                  value={profile.rank} 
                  onValueChange={(v) => setProfile(prev => ({ ...prev, rank: v }))}
                >
                  <SelectTrigger className="mt-1 dark:bg-navy-700 dark:border-navy-600">
                    <SelectValue placeholder="Select rank" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-navy-800 dark:border-navy-600">
                    {RANKS.map(rank => (
                      <SelectItem key={rank} value={rank}>{rank}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">MOS/Rate/AFSC</Label>
                <Input
                  value={profile.mos}
                  onChange={(e) => setProfile(prev => ({ ...prev, mos: e.target.value }))}
                  placeholder="e.g., 11B, HM, 1A8X1"
                  className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                  data-testid="profile-mos-input"
                />
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Years of Service</Label>
                <Input
                  type="number"
                  value={profile.years_of_service}
                  onChange={(e) => setProfile(prev => ({ ...prev, years_of_service: parseInt(e.target.value) || '' }))}
                  placeholder="e.g., 8"
                  className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                />
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Discharge Status</Label>
                <Select 
                  value={profile.discharge_status} 
                  onValueChange={(v) => setProfile(prev => ({ ...prev, discharge_status: v }))}
                >
                  <SelectTrigger className="mt-1 dark:bg-navy-700 dark:border-navy-600">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-navy-800 dark:border-navy-600">
                    <SelectItem value="honorable">Honorable</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="active">Still Active Duty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Work Experience */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                <Briefcase className="w-5 h-5 text-amber-600" />
                Work Experience
              </CardTitle>
              <Button onClick={openAddWorkModal} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="add-work-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Experience
              </Button>
            </CardHeader>
            <CardContent>
              {profile.work_experience.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-navy-200 dark:border-navy-600 rounded-lg">
                  <Briefcase className="w-10 h-10 text-navy-300 dark:text-navy-500 mx-auto mb-2" />
                  <p className="text-navy-500 dark:text-navy-400">No work experience added yet</p>
                  <Button onClick={openAddWorkModal} variant="link" className="text-amber-600 mt-2">
                    Add your first experience
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {profile.work_experience.map((work, index) => (
                    <div key={index} className="p-4 border border-navy-100 dark:border-navy-600 rounded-lg hover:border-amber-200 dark:hover:border-amber-800 transition-colors dark:bg-navy-700/50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-navy-900 dark:text-white">{work.title}</h4>
                          <div className="flex items-center gap-2 text-navy-600 dark:text-navy-300 text-sm mt-1">
                            <Building2 className="w-4 h-4" />
                            <span>{work.company}</span>
                            {work.location && (
                              <>
                                <span className="text-navy-300 dark:text-navy-500">•</span>
                                <MapPin className="w-4 h-4" />
                                <span>{work.location}</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-navy-500 dark:text-navy-400 text-sm mt-1">
                            <Calendar className="w-4 h-4" />
                            <span>{work.start_date} - {work.current ? 'Present' : work.end_date}</span>
                          </div>
                          {work.description && (
                            <p className="text-navy-600 dark:text-navy-300 text-sm mt-2 line-clamp-2">{work.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => openEditWorkModal(index)} className="h-8 w-8 text-navy-500 hover:text-amber-600">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteWorkExperience(index)} className="h-8 w-8 text-navy-500 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Education */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                <GraduationCap className="w-5 h-5 text-amber-600" />
                Education
              </CardTitle>
              <Button onClick={openAddEduModal} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="add-edu-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Education
              </Button>
            </CardHeader>
            <CardContent>
              {profile.education.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-navy-200 dark:border-navy-600 rounded-lg">
                  <GraduationCap className="w-10 h-10 text-navy-300 dark:text-navy-500 mx-auto mb-2" />
                  <p className="text-navy-500 dark:text-navy-400">No education added yet</p>
                  <Button onClick={openAddEduModal} variant="link" className="text-amber-600 mt-2">
                    Add your education
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {profile.education.map((edu, index) => (
                    <div key={index} className="p-4 border border-navy-100 dark:border-navy-600 rounded-lg hover:border-amber-200 dark:hover:border-amber-800 transition-colors dark:bg-navy-700/50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-navy-900 dark:text-white">{edu.degree}</h4>
                          {edu.field && (
                            <p className="text-navy-700 dark:text-navy-300 text-sm">{edu.field}</p>
                          )}
                          <div className="flex items-center gap-2 text-navy-600 dark:text-navy-400 text-sm mt-1">
                            <GraduationCap className="w-4 h-4" />
                            <span>{edu.school}</span>
                            {edu.location && (
                              <>
                                <span className="text-navy-300 dark:text-navy-500">•</span>
                                <MapPin className="w-4 h-4" />
                                <span>{edu.location}</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            {edu.year && (
                              <Badge variant="outline" className="text-xs dark:border-navy-500">{edu.year}</Badge>
                            )}
                            {edu.gpa && (
                              <Badge variant="outline" className="text-xs dark:border-navy-500">GPA: {edu.gpa}</Badge>
                            )}
                            {edu.honors && (
                              <Badge className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400">{edu.honors}</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => openEditEduModal(index)} className="h-8 w-8 text-navy-500 hover:text-amber-600">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteEducation(index)} className="h-8 w-8 text-navy-500 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Skills & Certifications */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                <GraduationCap className="w-5 h-5 text-amber-600" />
                Skills & Certifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-navy-700 dark:text-navy-300 mb-2 block">Skills</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="Add a skill"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    data-testid="profile-skill-input"
                    className="dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                  />
                  <Button onClick={addSkill} variant="outline" className="shrink-0 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map(skill => (
                    <Badge key={skill} variant="secondary" className="bg-navy-100 dark:bg-navy-700 text-navy-700 dark:text-navy-200 px-3 py-1">
                      {skill}
                      <button onClick={() => removeSkill(skill)} className="ml-2 hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-navy-700 dark:text-navy-300 mb-2 block">Certifications</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newCert}
                    onChange={(e) => setNewCert(e.target.value)}
                    placeholder="Add a certification"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                    className="dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                  />
                  <Button onClick={addCertification} variant="outline" className="shrink-0 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.certifications.map(cert => (
                    <Badge key={cert} variant="secondary" className="bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 px-3 py-1">
                      {cert}
                      <button onClick={() => removeCertification(cert)} className="ml-2 hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Career Goals */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                <Briefcase className="w-5 h-5 text-amber-600" />
                Career Goals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Professional Summary</Label>
                <Textarea
                  value={profile.summary}
                  onChange={(e) => setProfile(prev => ({ ...prev, summary: e.target.value }))}
                  placeholder="Brief overview of your background and career objectives..."
                  className="mt-1 min-h-[100px] dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                  data-testid="profile-summary-textarea"
                />
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300">Career Goals</Label>
                <Textarea
                  value={profile.career_goals}
                  onChange={(e) => setProfile(prev => ({ ...prev, career_goals: e.target.value }))}
                  placeholder="What are your career aspirations?"
                  className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                />
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300 mb-2 block">Preferred Industries</Label>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES.map(industry => (
                    <Badge
                      key={industry}
                      variant={profile.preferred_industries.includes(industry) ? "default" : "outline"}
                      className={`cursor-pointer transition-all ${
                        profile.preferred_industries.includes(industry) 
                          ? 'bg-amber-600 hover:bg-amber-700' 
                          : 'hover:bg-navy-50 dark:hover:bg-navy-700 dark:border-navy-600 dark:text-navy-300'
                      }`}
                      onClick={() => toggleIndustry(industry)}
                    >
                      {industry}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-navy-700 dark:text-navy-300">Remote Preference</Label>
                  <Select 
                    value={profile.remote_preference} 
                    onValueChange={(v) => setProfile(prev => ({ ...prev, remote_preference: v }))}
                  >
                    <SelectTrigger className="mt-1 dark:bg-navy-700 dark:border-navy-600">
                      <SelectValue placeholder="Select preference" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-navy-800 dark:border-navy-600">
                      <SelectItem value="remote">Remote Only</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="onsite">On-site</SelectItem>
                      <SelectItem value="any">No Preference</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-navy-700 dark:text-navy-300">Salary Expectation</Label>
                  <Input
                    value={profile.salary_expectation}
                    onChange={(e) => setProfile(prev => ({ ...prev, salary_expectation: e.target.value }))}
                    placeholder="e.g., $80,000 - $100,000"
                    className="mt-1 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-navy-700 dark:text-navy-300 mb-2 block">Preferred Locations</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    placeholder="Add a location"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                    className="dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                  />
                  <Button onClick={addLocation} variant="outline" className="shrink-0 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.preferred_locations.map(loc => (
                    <Badge key={loc} variant="secondary" className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 px-3 py-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      {loc}
                      <button 
                        onClick={() => setProfile(prev => ({ 
                          ...prev, 
                          preferred_locations: prev.preferred_locations.filter(l => l !== loc) 
                        }))} 
                        className="ml-2 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={saveProfile} 
              className="bg-amber-600 hover:bg-amber-700 px-8"
              disabled={saving}
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Work Experience Modal */}
        <Dialog open={showWorkModal} onOpenChange={setShowWorkModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingWorkIndex !== null ? 'Edit Work Experience' : 'Add Work Experience'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-navy-700">Job Title *</Label>
                <Input
                  value={workForm.title}
                  onChange={(e) => setWorkForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Project Manager"
                  className="mt-1"
                  data-testid="work-title-input"
                />
              </div>
              <div>
                <Label className="text-navy-700">Company *</Label>
                <Input
                  value={workForm.company}
                  onChange={(e) => setWorkForm(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="e.g., U.S. Army, Tech Corp"
                  className="mt-1"
                  data-testid="work-company-input"
                />
              </div>
              <div>
                <Label className="text-navy-700">Location</Label>
                <Input
                  value={workForm.location}
                  onChange={(e) => setWorkForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Fort Bragg, NC"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-navy-700">Start Date</Label>
                  <Input
                    value={workForm.start_date}
                    onChange={(e) => setWorkForm(prev => ({ ...prev, start_date: e.target.value }))}
                    placeholder="e.g., Jan 2018"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-navy-700">End Date</Label>
                  <Input
                    value={workForm.end_date}
                    onChange={(e) => setWorkForm(prev => ({ ...prev, end_date: e.target.value }))}
                    placeholder="e.g., Dec 2022"
                    className="mt-1"
                    disabled={workForm.current}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="currentJob"
                  checked={workForm.current}
                  onChange={(e) => setWorkForm(prev => ({ ...prev, current: e.target.checked }))}
                  className="rounded border-navy-300"
                />
                <Label htmlFor="currentJob" className="text-navy-700 cursor-pointer">I currently work here</Label>
              </div>
              <div>
                <Label className="text-navy-700">Description</Label>
                <Textarea
                  value={workForm.description}
                  onChange={(e) => setWorkForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your responsibilities and achievements..."
                  className="mt-1 min-h-[100px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWorkModal(false)}>Cancel</Button>
              <Button onClick={saveWorkExperience} className="bg-amber-600 hover:bg-amber-700" data-testid="save-work-btn">
                {editingWorkIndex !== null ? 'Update' : 'Add'} Experience
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Education Modal */}
        <Dialog open={showEduModal} onOpenChange={setShowEduModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingEduIndex !== null ? 'Edit Education' : 'Add Education'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-navy-700">Degree Type *</Label>
                <Select 
                  value={eduForm.degree} 
                  onValueChange={(v) => setEduForm(prev => ({ ...prev, degree: v }))}
                >
                  <SelectTrigger className="mt-1" data-testid="edu-degree-select">
                    <SelectValue placeholder="Select degree type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEGREE_TYPES.map(degree => (
                      <SelectItem key={degree} value={degree}>{degree}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-navy-700">Field of Study</Label>
                <Input
                  value={eduForm.field}
                  onChange={(e) => setEduForm(prev => ({ ...prev, field: e.target.value }))}
                  placeholder="e.g., Business Administration"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-navy-700">School / Institution *</Label>
                <Input
                  value={eduForm.school}
                  onChange={(e) => setEduForm(prev => ({ ...prev, school: e.target.value }))}
                  placeholder="e.g., University of Phoenix"
                  className="mt-1"
                  data-testid="edu-school-input"
                />
              </div>
              <div>
                <Label className="text-navy-700">Location</Label>
                <Input
                  value={eduForm.location}
                  onChange={(e) => setEduForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Phoenix, AZ"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-navy-700">Graduation Year</Label>
                  <Input
                    value={eduForm.year}
                    onChange={(e) => setEduForm(prev => ({ ...prev, year: e.target.value }))}
                    placeholder="e.g., 2022"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-navy-700">GPA (Optional)</Label>
                  <Input
                    value={eduForm.gpa}
                    onChange={(e) => setEduForm(prev => ({ ...prev, gpa: e.target.value }))}
                    placeholder="e.g., 3.8"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-navy-700">Honors / Awards</Label>
                <Input
                  value={eduForm.honors}
                  onChange={(e) => setEduForm(prev => ({ ...prev, honors: e.target.value }))}
                  placeholder="e.g., Magna Cum Laude, Dean's List"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEduModal(false)}>Cancel</Button>
              <Button onClick={saveEducation} className="bg-amber-600 hover:bg-amber-700" data-testid="save-edu-btn">
                {editingEduIndex !== null ? 'Update' : 'Add'} Education
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ProfileBuilder;
